<template>
  <div ref="root" class="sd" :class="{ 'is-open': open, 'is-disabled': disabled }">
    <!-- Toggle Button -->
    <button
        class="sd-btn"
        type="button"
        @click.stop="toggle"
        :disabled="disabled"
        :aria-expanded="open"
        aria-haspopup="listbox"
    >
      <span class="sd-title">{{ selected?.title || placeholder }}</span>
      <span class="sd-price">{{ selected?.priceLabel || '—' }}</span>
      <span class="sd-caret" aria-hidden="true">▾</span>
    </button>

    <!-- Popover -->
    <div
        v-if="open"
        class="sd-pop"
        role="listbox"
        tabindex="-1"
        @keydown.esc.stop.prevent="close('esc')"
    >
      <button
          v-for="o in optionsSafe"
          :key="o.key"
          type="button"
          class="sd-opt"
          :class="{ active: o.key === modelValue }"
          role="option"
          :aria-selected="o.key === modelValue"
          @click.stop="pick(o.key)"
          @mousedown.stop
      >
        <span class="sd-opt-title">{{ o.title }}</span>
        <span class="sd-opt-price">{{ o.priceLabel || '—' }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, type PropType, ref, watch} from 'vue'

export type SizePriceOption = {
  key: string
  title: string
  price: number
  priceLabel?: string
}

const props = defineProps({
  options: { type: Array as PropType<SizePriceOption[]>, default: () => [] },
  modelValue: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  placeholder: { type: String, default: '— wählen —' },
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const open = ref(false)
const root = ref<HTMLElement | null>(null)

const optionsSafe = computed(() => props.options ?? [])
const selected = computed(() => optionsSafe.value.find(o => o.key === props.modelValue) ?? null)

function close(_reason: string = 'close') {
  open.value = false
}

function toggle() {
  if (props.disabled) return
  open.value = !open.value
}

async function pick(key: string) {
  // Emit zuerst, dann sicher schließen (und nicht wieder öffnen durch bubbling/timing)
  emit('update:modelValue', key)

  // "hart" schließen im nächsten Micro-/Render-Tick
  await nextTick()
  close('pick')
}

function onDocPointerDown(e: MouseEvent) {
  const el = root.value
  if (!el) return
  if (!el.contains(e.target as Node)) close('outside')
}

watch(open, v => {
  if (v) document.addEventListener('mousedown', onDocPointerDown)
  else document.removeEventListener('mousedown', onDocPointerDown)
})

onBeforeUnmount(() => document.removeEventListener('mousedown', onDocPointerDown))
</script>

<style scoped lang="scss">
/* Minimal, modern, light */
.sd{ position:relative; width:100%; min-width:0; }
.sd.is-disabled{ opacity:.7; }

.sd-btn{
  width:100%;
  height:36px;
  display:flex;
  align-items:center;
  gap:10px;
  padding:0 10px;
  border:1px solid rgba(15,23,42,.10);
  background:#fff;
  border-radius:3px;
  cursor:pointer;
  min-width:0;
}
.sd-btn:disabled{ cursor:not-allowed; }
.sd.is-open .sd-btn{
  border-color: rgba(37,99,235,.55);
  box-shadow: 0 0 0 3px rgba(37,99,235,.12);
}

.sd-title{
  flex:1 1 auto;
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
  font-weight: 700;
  font-size:13px;
  color:#0f172a;
}
.sd-price{
  font-size:12px;
  color:#64748b;
  font-weight: 700;
  white-space:nowrap;
}
.sd-caret{ font-size:12px; color:#64748b; }

.sd-pop{
  position:absolute;
  left:0; right:0;
  top: calc(100% + 8px);
  z-index: 50;
  background:#fff;
  border: 1px solid rgba(15,23,42,.10);
  border-radius: 12px;
  box-shadow: 0 14px 40px rgba(2,6,23,.10);
  overflow:hidden;
  max-height: 260px;
  overflow-y:auto;
}

.sd-opt{
  width:100%;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  padding:10px 12px;
  border:none;
  background:#fff;
  cursor:pointer;
  text-align:left;
}
.sd-opt:hover{ background: rgba(2,6,23,.03); }
.sd-opt.active{ background: rgba(37,99,235,.10); }

.sd-opt-title{ font-weight: 700; font-size:13px; color:#0f172a; }
.sd-opt-price{ font-size:12px; color:#64748b; font-weight: 700; white-space:nowrap; }
</style>
